import { crmApi } from '@/api/queries/crm'
import { RequestCreateLeadPaymentLink } from '@/api/types/crm/crm.types'
import { isError } from '@/helpers/checkQuery'
import {
  getIndividualLessonLinkSubmitError,
  mapIndividualLessonFieldsForSubmit,
  mapLessonAdditionalCourseForSubmit,
  mapSimpleAdditionalCourseForSubmit,
  resolveIndividualMainSubmitPrice,
} from './components/IndividualLessonLinkFields/individualLessonLink.utils'
import {
  isClassDiscountInvalid,
  isMonthlyAdditionalCourseDiscountInvalid,
  isMonthlyCourseLinkTariff,
  isMonthlyDiscountInvalid,
  mapMonthlyAdditionalCourseForSubmit,
  normalizeMonthlyPeriodUnit,
  normalizeMonthlyPeriodValue,
  resolveMonthlyMainSubmitPrice,
  resolveMonthlyPeriodUnitForSubmit,
} from './components/MonthlyCourseLinkFields/monthlyCourseLink.utils'
import {
  isStreamAdditionalCourseDiscountInvalid,
  isStreamDiscountInvalid,
  mapStreamAdditionalCourseForSubmit,
  resolveStreamMainSubmitPrice,
  resolveStreamPeriodValue,
} from './components/StreamCustomPeriodLinkFields/streamCustomPeriodLink.utils'
import { parseAndValidateAddedCourses } from './components/SelectAdditionalCourses/utils/parseAddedCourses'
import {
  discountModeToApplyEachPayment,
  resolveCrmLinkDiscountMode,
  type CrmLinkDiscountMode,
} from './discountMode.utils'
import {
  isLessonPaymentTariff,
  isStreamCustomPeriodTariff,
} from './tariffTypes'
import type { CreateLinkFieldType } from './createLinkModal.types'

type AlertPayload = {
  title: string
  text: string | string[]
  type: 'error' | 'success'
}

export type CreateLinkSubmitContext = {
  currentLeadFunnelId?: number
  leadId: number
  selectedTariff?: ICourseTariff | null
  selectedClass?: { one_pay?: boolean | number | null; amount?: unknown } | null
  courses?: Array<{
    id?: number | null
    name?: string | null
    directionTariffs?: ICourseTariff[] | null
  }> | null
  isPeriodFixed: boolean
  canSelectCourseGroup: boolean
  isGroupPerLessonTariff: boolean
  monthlyCourseBasePrice: number
  classBasePrice: number
  isIndividualLinkActive: boolean
}

const buildCreateLinkPayload = (
  values: CreateLinkFieldType,
  ctx: CreateLinkSubmitContext,
): { error: string } | { payload: RequestCreateLeadPaymentLink } => {
  if (!ctx.currentLeadFunnelId) {
    return { error: 'Не удалось определить воронку для создания ссылки' }
  }

  const isMonthlyCourse =
    values.type === 'course' &&
    isMonthlyCourseLinkTariff(ctx.selectedTariff?.type)
  const isStreamCustomPeriod =
    values.type === 'course' &&
    isStreamCustomPeriodTariff(ctx.selectedTariff?.type)
  const isClassLink =
    values.type === 'class' && Boolean(ctx.selectedClass?.one_pay)
  const discountValue = Number(values.discount) || 0
  // Числа со слайдера/инпута всегда уходят в value.
  // Фиксация для модалки записи = наличие period_unit (null = покупатель может менять слайдером).
  const monthlyPeriodUnit = normalizeMonthlyPeriodUnit(values.period_unit)
  const monthlyPeriodValue = normalizeMonthlyPeriodValue(values.value)
  const streamPeriodForPrice = resolveStreamPeriodValue(
    values.value,
    ctx.selectedTariff,
    isStreamCustomPeriod,
  )

  const individualSubmitError = getIndividualLessonLinkSubmitError(
    ctx.isIndividualLinkActive,
    values,
  )
  if (individualSubmitError) {
    return { error: individualSubmitError }
  }

  if (
    isMonthlyCourse &&
    isMonthlyDiscountInvalid(ctx.monthlyCourseBasePrice, discountValue)
  ) {
    return {
      error: 'Скидка должна быть меньше стоимости первого месяца тарифа',
    }
  }

  if (
    isStreamCustomPeriod &&
    isStreamDiscountInvalid(
      ctx.selectedTariff,
      streamPeriodForPrice,
      discountValue,
    )
  ) {
    return { error: 'Скидка должна быть меньше стоимости периода тарифа' }
  }

  if (
    isClassLink &&
    isClassDiscountInvalid(ctx.classBasePrice, discountValue)
  ) {
    return { error: 'Скидка должна быть меньше месячной стоимости класса' }
  }

  const additionalCourses = parseAndValidateAddedCourses(
    values.additional_courses ?? null,
  ).map((item) => {
    const tariff = ctx.courses
      ?.find((course) => course.id === item.courseId)
      ?.directionTariffs?.find((itemTariff) => itemTariff.id === item.tariffId)

    if (tariff && isStreamCustomPeriodTariff(tariff.type)) {
      return mapStreamAdditionalCourseForSubmit(item, tariff) as typeof item
    }

    if (tariff && isMonthlyCourseLinkTariff(tariff.type)) {
      return mapMonthlyAdditionalCourseForSubmit(item) as typeof item
    }

    if (tariff && isLessonPaymentTariff(tariff.type)) {
      return mapLessonAdditionalCourseForSubmit(item, tariff.type) as typeof item
    }

    return mapSimpleAdditionalCourseForSubmit(item) as typeof item
  })

  const invalidAdditionalCourseDiscount = additionalCourses.find((item) => {
    if (!item.courseId || !item.tariffId) return false

    const tariff = ctx.courses
      ?.find((course) => course.id === item.courseId)
      ?.directionTariffs?.find((tariff) => tariff.id === item.tariffId)

    const discount = Number(item.discount) || 0
    if (discount <= 0) return false

    if (isMonthlyAdditionalCourseDiscountInvalid(item, tariff)) {
      return true
    }

    return isStreamAdditionalCourseDiscountInvalid(item, tariff)
  })

  if (invalidAdditionalCourseDiscount) {
    const course = ctx.courses?.find(
      (item) => item.id === invalidAdditionalCourseDiscount.courseId,
    )
    const tariff = course?.directionTariffs?.find(
      (item) => item.id === invalidAdditionalCourseDiscount.tariffId,
    )
    const courseName =
      course?.name?.trim() ||
      `курс #${invalidAdditionalCourseDiscount.courseId}`
    const tariffName =
      tariff?.name?.trim() ||
      `тариф #${invalidAdditionalCourseDiscount.tariffId}`

    return {
      error: `Скидка у дополнительного курса должна быть меньше стоимости тарифа/периода (${courseName} — ${tariffName})`,
    }
  }

  const monthlyPrice = resolveMonthlyMainSubmitPrice(
    isMonthlyCourse,
    values.price,
  )
  const streamCustomPeriodPrice = resolveStreamMainSubmitPrice(
    isStreamCustomPeriod,
    values.price,
  )
  const classPrice = resolveMonthlyMainSubmitPrice(isClassLink, values.price)
  const individualPrice = resolveIndividualMainSubmitPrice(
    ctx.isIndividualLinkActive,
    values.price,
  )
  const individualFields = mapIndividualLessonFieldsForSubmit(
    ctx.isIndividualLinkActive,
    values,
  )

  return {
    payload: {
      ...values,
      is_show_hidden_tariffs: true,
      additional_courses: additionalCourses.length
        ? JSON.stringify(additionalCourses)
        : null,
      price:
        monthlyPrice != null
          ? monthlyPrice
          : streamCustomPeriodPrice != null
          ? streamCustomPeriodPrice
          : classPrice != null
          ? classPrice
          : individualPrice != null
          ? individualPrice
          : values.price === null || values.price === undefined
          ? null
          : Number(values.price),
      ...individualFields,
      discount:
        isMonthlyCourse || isClassLink || isStreamCustomPeriod
          ? discountValue || null
          : ctx.isGroupPerLessonTariff && !Boolean(values.is_value_fixed)
          ? discountValue || null
          : null,
      discount_mode: (() => {
        // gpl без фиксации кол-ва: скидка есть → first_only (типа в UI нет).
        if (
          ctx.isGroupPerLessonTariff &&
          !Boolean(values.is_value_fixed) &&
          discountValue > 0
        ) {
          return 'first_only'
        }
        if (
          !(isMonthlyCourse || isClassLink || isStreamCustomPeriod) ||
          !(discountValue > 0)
        ) {
          return null
        }
        return resolveCrmLinkDiscountMode({
          discount: discountValue,
          discountMode: values.discount_mode as CrmLinkDiscountMode | null,
          applyDiscountEachPayment: values.apply_discount_each_payment,
          isPeriodFixed: ctx.isPeriodFixed,
        })
      })(),
      apply_discount_each_payment: (() => {
        if (
          ctx.isGroupPerLessonTariff &&
          !Boolean(values.is_value_fixed) &&
          discountValue > 0
        ) {
          return false
        }
        if (
          !(isMonthlyCourse || isClassLink || isStreamCustomPeriod) ||
          !(discountValue > 0)
        ) {
          return false
        }
        return discountModeToApplyEachPayment(
          resolveCrmLinkDiscountMode({
            discount: discountValue,
            discountMode: values.discount_mode as CrmLinkDiscountMode | null,
            applyDiscountEachPayment: values.apply_discount_each_payment,
            isPeriodFixed: ctx.isPeriodFixed,
          }),
        )
      })(),
      period_unit: resolveMonthlyPeriodUnitForSubmit(
        isMonthlyCourse || isClassLink || isStreamCustomPeriod,
        ctx.isPeriodFixed,
        monthlyPeriodUnit,
      ),
      value:
        isMonthlyCourse || isClassLink || isStreamCustomPeriod
          ? monthlyPeriodValue
          : values.value,
      is_value_fixed: isLessonPaymentTariff(ctx.selectedTariff?.type)
        ? Boolean(values.is_value_fixed)
        : false,
      group_id:
        ctx.canSelectCourseGroup && values.group_id != null
          ? Number(values.group_id)
          : null,
      lead_id: ctx.leadId,
      lead_funnel_id: ctx.currentLeadFunnelId,
    },
  }
}

export const submitCreateLeadPaymentLink = ({
  values,
  ctx,
  addAlert,
  onSuccess,
  setSubmitting,
}: {
  values: CreateLinkFieldType
  ctx: CreateLinkSubmitContext
  addAlert: (payload: AlertPayload) => void
  onSuccess?: () => void
  setSubmitting: (value: boolean) => void
}) => {
  const built = buildCreateLinkPayload(values, ctx)
  if ('error' in built) {
    addAlert({
      title: 'Ошибка',
      text: built.error,
      type: 'error',
    })
    return
  }

  setSubmitting(true)
  crmApi
    .createLeadPaymentLink(built.payload)
    .then((res) => {
      if (isError(res)) throw new Error()

      addAlert({
        title: 'Успешно',
        text: 'Ссылка успешно создана',
        type: 'success',
      })
      onSuccess?.()
    })
    .catch((error: any) => {
      const discountError = error?.response?.data?.data?.discount
      const discountMessage = Array.isArray(discountError)
        ? discountError[0]
        : typeof discountError === 'string'
        ? discountError
        : null

      addAlert({
        title: 'Ошибка',
        text: discountMessage || 'Не удалось создать ссылку',
        type: 'error',
      })
    })
    .finally(() => setSubmitting(false))
}

export const getCreateLinkSubmitFailedMessages = (errorInfo: {
  errorFields: Array<{ name: any; errors: string[] }>
}) => {
  const fieldLabels: Record<string, string> = {
    type: 'Тип',
    class_id: 'Класс',
    course_id: 'Курс',
    tariff_id: 'Тариф',
    price: 'Цена',
    value: 'Период',
    period_unit: 'Единица периода',
    discount: 'Скидка',
    first_price: 'Цена занятия',
    lesson_duration: 'Длительность занятия',
    teacher_id: 'Педагог',
    salary_per_hour: 'ЗП педагога',
  }

  return errorInfo.errorFields.map((field) => {
    const fieldName = Array.isArray(field.name) ? field.name[0] : field.name
    const label = fieldLabels[fieldName as string] || fieldName
    return `${label}: ${field.errors.join(', ')}`
  })
}
